import { Component, OnInit, Inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'app-referral-lang-dialog',
    template: `
    <div class="lang-dialog" dir="rtl">
      <h3>בחר שפה להדפסה</h3>
      <div class="lang-btns">
        <button class="btn-lang" (click)="select('he')">עברית</button>
        <button class="btn-lang" (click)="select('en')">English</button>
      </div>
      <div class="lang-cancel">
        <button class="btn-cancel" (click)="cancel()">ביטול</button>
      </div>
    </div>
  `,
    styles: [`
    .lang-dialog { padding: 24px 32px; min-width: 260px; text-align: center; }
    h3 { margin: 0 0 20px; font-size: 1.1em; color: #022931; }
    .lang-btns { display: flex; gap: 12px; justify-content: center; margin-bottom: 16px; }
    .btn-lang { padding: 10px 28px; border: 1.5px solid #022931; background: #fff; color: #022931; border-radius: 6px; font-size: 1em; cursor: pointer; font-family: inherit; transition: background 0.15s; }
    .btn-lang:hover { background: #ebf1f1; }
    .btn-cancel { background: none; border: none; color: #888; cursor: pointer; font-size: 0.9em; text-decoration: underline; }
  `],
    standalone: false
})
export class ReferralLangDialogComponent {
  constructor(private ref: MatDialogRef<ReferralLangDialogComponent>) {}
  select(lang: string) { this.ref.close(lang); }
  cancel() { this.ref.close(null); }
}

import { BloodTestReferralService, ReferralSaveItem } from '../blood-test-referral.service';
import { LoginService } from '../login.service';
import { CATEGORIES, ReferralCategory } from './blood-test-referral-lookups';

export interface ReferralDialogData {
  PID: string;
  GID: number;
  patientName: string;
  patientID: string;
  patientDOB: string;
  HMO: string;
  examID?: number;
  existingReferralId?: number;
}

@Component({
    selector: 'app-blood-test-referral-dialog',
    templateUrl: './blood-test-referral-dialog.component.html',
    styleUrls: ['./blood-test-referral-dialog.component.scss'],
    standalone: false
})
export class BloodTestReferralDialogComponent implements OnInit {

  categories: ReferralCategory[] = CATEGORIES;
  selected: { [key: string]: boolean } = {};
  expanded: { [key: number]: boolean } = {};
  instructionsCtrl = new FormControl('');

  referralId: number = null;
  status = 'draft';
  signedAt: string = null;
  createdAt: string = null;
  loading = false;
  saving = false;
  signing = false;
  editing = false;
  deleting = false;
  confirmingDelete = false;
  errorMsg = '';
  signerName = '';
  signerTitle = '';

  private hmoMap: { [key: string]: string } = {
    'Clalit': 'כללית', 'Macabi': 'מכבי', 'Meuhedet': 'מאוחדת',
    'Leumit': 'לאומית', 'Private': 'פרטי', 'IDF': 'צבאי',
  };

  getHMO(hmo: string): string { return this.hmoMap[hmo] || hmo; }

  constructor(
    public dialogRef: MatDialogRef<BloodTestReferralDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReferralDialogData,
    private referralService: BloodTestReferralService,
    public ls: LoginService,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    if (this.data.existingReferralId) {
      this.loadExisting(this.data.existingReferralId);
    } else if (this.data.examID) {
      this.loading = true;
      this.referralService.getByExam(this.data.examID).subscribe(
        existing => {
          if (existing && existing.AutoIndex) {
            this.loadExisting(existing.AutoIndex);
          } else {
            this.createDraft();
          }
        },
        () => this.createDraft()
      );
    } else {
      this.createDraft();
    }
  }

  private createDraft() {
    this.loading = true;
    this.referralService.create(this.data.PID, this.data.GID, this.data.examID).subscribe(
      res => { this.referralId = res.AutoIndex; this.createdAt = new Date().toISOString(); this.loading = false; },
      () => { this.errorMsg = 'שגיאה ביצירת ההפניה'; this.loading = false; }
    );
  }

  private loadExisting(id: number) {
    this.referralId = id;
    this.loading = true;
    this.referralService.getOne(id).subscribe(
      res => {
        this.status = res.Status || 'draft';
        this.signedAt = res.SignedAt;
        this.createdAt = res.CreatedAt;
        this.signerName = res.Opener || '';
        this.signerTitle = res.OpenerTitle || '';
        this.instructionsCtrl.setValue(res.Instructions || '');
        this.applySelection(res.items || []);
        this.loading = false;
      },
      () => { this.errorMsg = 'שגיאה בטעינת ההפניה'; this.loading = false; }
    );
  }

  private applySelection(items: { Category?: string; TestName?: string; TestCode?: string }[]) {
    this.selected = {};
    items.forEach(it => {
      this.categories.forEach((cat, ci) => {
        if (cat.name !== it.Category) return;
        cat.tests.forEach((t, ti) => {
          if (t.name === it.TestName && (t.code || '') === (it.TestCode || '')) {
            this.selected[ci + ':' + ti] = true;
          }
        });
      });
    });
  }

  private get readonly(): boolean {
    return (this.status === 'signed' || this.status === 'printed') && !this.editing;
  }

  isSigned(): boolean { return this.status === 'signed' || this.status === 'printed'; }
  isLocked(): boolean { return this.readonly; }

  startEdit() { this.editing = true; }

  key(ci: number, ti: number): string { return ci + ':' + ti; }
  isSelected(ci: number, ti: number): boolean { return !!this.selected[this.key(ci, ti)]; }

  hasSelection(ci: number): boolean {
    return this.categories[ci].tests.some((_, ti) => this.isSelected(ci, ti));
  }

  groupSelectedCount(ci: number): number {
    return this.categories[ci].tests.filter((_, ti) => this.isSelected(ci, ti)).length;
  }

  // A category is open when explicitly expanded, or forced open because it holds a selection.
  isExpanded(ci: number): boolean {
    return !!this.expanded[ci] || this.hasSelection(ci);
  }

  toggleExpand(ci: number) {
    // A category with a selection cannot be collapsed.
    if (this.hasSelection(ci)) { this.expanded[ci] = true; return; }
    this.expanded[ci] = !this.expanded[ci];
  }

  toggleTest(ci: number, ti: number) {
    if (this.readonly) return;
    const k = this.key(ci, ti);
    this.selected[k] = !this.selected[k];
  }

  isGroupAll(ci: number): boolean {
    const tests = this.categories[ci].tests;
    return tests.length > 0 && tests.every((_, ti) => this.isSelected(ci, ti));
  }

  isGroupSome(ci: number): boolean {
    const tests = this.categories[ci].tests;
    const n = tests.filter((_, ti) => this.isSelected(ci, ti)).length;
    return n > 0 && n < tests.length;
  }

  toggleGroup(ci: number) {
    if (this.readonly) return;
    const all = this.isGroupAll(ci);
    this.categories[ci].tests.forEach((_, ti) => { this.selected[this.key(ci, ti)] = !all; });
  }

  get selectedCount(): number {
    return Object.keys(this.selected).filter(k => this.selected[k]).length;
  }

  private buildItems(): ReferralSaveItem[] {
    const out: ReferralSaveItem[] = [];
    this.categories.forEach((cat, ci) => {
      cat.tests.forEach((t, ti) => {
        if (this.isSelected(ci, ti)) {
          out.push({ category: cat.name, name: t.name, code: t.code });
        }
      });
    });
    return out;
  }

  sign() {
    if (!this.referralId) return;
    if (this.selectedCount === 0) { this.errorMsg = 'יש לבחור לפחות בדיקה אחת'; return; }
    this.errorMsg = '';
    this.signing = true;
    const saveAndSign = () => {
      this.referralService.save(this.referralId, this.buildItems(), (this.instructionsCtrl.value || '').toString()).subscribe(
        () => this.doSign(),
        () => { this.errorMsg = 'שגיאה בשמירת ההפניה'; this.signing = false; }
      );
    };
    // Editing a previously signed referral: reopen it to draft so the edited items can be saved, then re-sign.
    if (this.editing && (this.status === 'signed' || this.status === 'printed')) {
      this.referralService.updateStatus(this.referralId, 'draft').subscribe(
        () => { this.status = 'draft'; saveAndSign(); },
        () => { this.errorMsg = 'שגיאה בפתיחת ההפניה לעריכה'; this.signing = false; }
      );
    } else {
      saveAndSign();
    }
  }

  private doSign() {
    this.referralService.sign(this.referralId).subscribe(
      (res: any) => {
        this.status = 'signed';
        this.signedAt = new Date().toISOString();
        this.signerName = this.ls.username;
        this.signerTitle = (res && res.title) ? res.title : '';
        this.editing = false;
        this.signing = false;
      },
      (err) => {
        this.errorMsg = (err && err.error && err.error.message) ? err.error.message : 'שגיאה בחתימת ההפניה';
        this.signing = false;
      }
    );
  }

  openPrint() {
    const ref = this.dialog.open(ReferralLangDialogComponent, { width: '320px', panelClass: 'rx-lang-dialog' });
    ref.afterClosed().subscribe(lang => { if (lang) { this.print(lang); } });
  }

  print(lang: string) {
    this.referralService.updateStatus(this.referralId, 'printed').subscribe();
    window.open(`/api/referrals/printpdf/${this.referralId}/${lang}`, '_blank');
  }

  askDelete() {
    if (!this.referralId) return;
    this.confirmingDelete = true;
  }

  cancelDelete() {
    this.confirmingDelete = false;
  }

  confirmDelete() {
    if (!this.referralId) return;
    this.deleting = true;
    this.errorMsg = '';
    this.referralService.deleteReferral(this.referralId).subscribe(
      () => {
        this.deleting = false;
        this.confirmingDelete = false;
        // Notify the opener that the referral was deleted, then close the dialog.
        this.dialogRef.close({ referralId: null, status: 'deleted', deleted: true });
      },
      () => {
        this.errorMsg = 'שגיאה במחיקת ההפניה';
        this.deleting = false;
        this.confirmingDelete = false;
      }
    );
  }

  close() {
    // Draft cleanup on close: save if the referral has content, otherwise delete the empty draft.
    if (this.referralId && !this.readonly) {
      const hasContent = this.selectedCount > 0 || (this.instructionsCtrl.value || '').toString().trim().length > 0;
      if (hasContent) {
        this.saving = true;
        this.referralService.save(this.referralId, this.buildItems(), (this.instructionsCtrl.value || '').toString()).subscribe(
          () => this.dialogRef.close({ referralId: this.referralId, status: this.status }),
          () => { this.saving = false; this.dialogRef.close({ referralId: this.referralId, status: this.status }); }
        );
      } else {
        this.referralService.deleteReferral(this.referralId).subscribe(
          () => this.dialogRef.close({ referralId: null, status: 'deleted' }),
          () => this.dialogRef.close({ referralId: this.referralId, status: this.status })
        );
      }
      return;
    }
    this.dialogRef.close({ referralId: this.referralId, status: this.status });
  }
}
